import { ActiveState, EntityState } from '@datorama/akita';
import { ListViewData } from '@y42/core/list-view';
import { AbstractFeatureChildUIModel } from '@y42/feature';
import { ColumnTypeEnum, JobStatus } from '@y42/json-schema';
import { UserUIModel } from '@y42/user/models';
import { JobStatusCode } from './job-status';

export enum TriggeredByType {
  User = 'user',
  Orchestration = 'orchestration',
}

export enum ImportWriteType {
  Truncate = 'TRUNCATE',
  Append = 'APPEND',
}

export interface JobContext {
  company_id: string;
  company_slug: string;
  triggered_by_type: TriggeredByType;
  triggered_by_id: string;
  trial: boolean;
}

export interface JobRequest {
  job_id: string;
  created_at: string;
  event_id?: string;
  target: JobTarget;
  context: JobContext;
  table_settings: {
    datasource_table_path?: string;
    [key: string]: unknown;
  };
  source_settings?: { [key: string]: unknown };
  integration_secret_id: string;
  environment_secret_id?: string;
  storage: {
    branch: string;
    commit_sha: string;
    company_id: string;
    id: string;
    path: string;
  };
}

export interface ColumnDefinition {
  field_name: string;
  data_type: ColumnTypeEnum;
  external_data_type?: string;
}

export interface JobTarget {
  info: { [key: string]: unknown };
}

export interface JobDetails {
  row_count?: number;
  size_in_bytes?: number;
  export_finished_at?: string;
  import_finished_at?: string;
  current_import_row_count?: number;
  external_state?: {};
  schema_hash?: string;
  write_disposition?: ImportWriteType;
  previous_id?: string;
  external_id?: string;
  storage_file?: string;
  table_name?: string;
  external_url?: string;
  partial?: boolean;
  columns?: ColumnDefinition[];
}

export interface JobHttpModel<Request extends JobRequest = JobRequest, Details extends JobDetails = JobDetails> {
  path: string;
  company_id: string;
  created_at: string;
  details: Details;
  error?: {
    message: string;
  };
  finished_at?: string;
  request: Request;
  request_id: string;
  status: JobStatus;
  import_status: JobStatus;
  parent_job_id: string;
}

export type JobWithParentHttpModel<
  Request extends JobRequest = JobRequest,
  Details extends JobDetails = JobDetails,
> = JobHttpModel<Request, Details> & { parent_job?: JobHttpModel<Request, Details> };

export interface JobHttpResponse<Model extends JobHttpModel = JobHttpModel> {
  jobs: Model[];
  cursor?: string;
}

export interface JobStoreModel<Request extends JobRequest = JobRequest, Details extends JobDetails = JobDetails>
  extends JobHttpModel<Request, Details> {}

export interface JobTriggeredByUser {
  type: TriggeredByType.User;
  data?: UserUIModel;
}
export interface JobTriggeredByOrchestration {
  type: TriggeredByType.Orchestration;
  data: string;
}

export type JobTriggeredBy = JobTriggeredByUser | JobTriggeredByOrchestration;

export interface JobUIModel extends JobStoreModel {
  triggeredBy: JobTriggeredBy;
  settingsValidationResult: JobSettingsValidationResults | undefined;
  settingsValid: boolean;
}

export interface JobSortFilter {
  searchKey: string;
  statusFilter: JobStatusCode | null;
}

export type JobListViewData = ListViewData<
  JobStoreModel &
    JobUIModel & {
      datasourceName: string;
      datasourceTableName: string;
      datasourceTypeName: string;
      datasourceTypeLogo: string;
      tags: string[];
    }
>;

export interface JobState<Model extends JobStoreModel = JobStoreModel>
  extends EntityState<Model, Model['request_id']>,
    ActiveState<Model['request_id']> {
  ui: {
    lastFetched: Date | null;

    sortFilter: Record<string, JobSortFilter>;
  };
}

export interface JobbableUIModel<JobModel extends JobUIModel = JobUIModel> extends AbstractFeatureChildUIModel {
  jobs: JobModel[];
  latest_job?: JobModel;
  latest_valid_job?: JobModel;
  latest_valid_chain_parent?: JobModel;
}

export type JobSettingsValidationType = 'source_settings' | 'table_settings';

export interface JobSettingsValidationResult {
  entityPath: string;
  filePath: string;
  content1AsJson: { data: string | Record<string, unknown>; props?: string[] };
  content2AsJson: { data: string | Record<string, unknown>; props?: string[] };
  isValid: boolean;
}

export type JobSettingsValidationResults<
  T2 extends string = never,
  T1 extends string = JobSettingsValidationType,
> = Record<T1 | T2, JobSettingsValidationResult>;

export interface JobDateRange {
  from: Date;
  to: Date;
}
